import type { ButtonHTMLAttributes, ReactNode } from "react";
import Icon from "../Icon/Icon";
import "./OptionCard.css";

export interface OptionCardProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "title"
> {
  /** round icon on the left */
  icon?: ReactNode;
  /** colour of the icon circle */
  iconTone?: "lime" | "ink" | "muted";
  title: ReactNode;
  description?: ReactNode;
  /** "highlight" = lime border (recommended choice), "dashed" = add/create */
  variant?: "default" | "highlight" | "dashed";
  /** dims the card while its action runs */
  busy?: boolean;
  /** extra content under the description, e.g. member avatars */
  children?: ReactNode;
}

/**
 * Big clickable choice with icon, title, text and a chevron.
 * Example: <OptionCard icon={<Icon name="user" />} title="Alone" onClick={fn} />
 */
const OptionCard = ({
  icon,
  iconTone = "lime",
  title,
  description,
  variant = "default",
  busy = false,
  children,
  className = "",
  type = "button",
  disabled,
  ...rest
}: OptionCardProps) => (
  <button
    type={type}
    className={`ds-option-card ds-option-card--${variant} ${
      busy ? "is-busy" : ""
    } ${className}`.trim()}
    disabled={disabled || busy}
    aria-busy={busy || undefined}
    {...rest}
  >
    {icon && (
      <span
        className={`ds-option-card__icon ds-option-card__icon--${iconTone}`}
      >
        {icon}
      </span>
    )}
    <span className="ds-option-card__text">
      <span className="ds-option-card__title">{title}</span>
      {description && (
        <span className="ds-option-card__description">{description}</span>
      )}
      {children}
    </span>
    <Icon name="chevronRight" size={16} className="ds-option-card__chevron" />
  </button>
);

export default OptionCard;
